<!-- Форма новой пометки и редактирования пометок ТС. -->
<template>
  <form :id="formId" class="vehicle-remark-form" @submit.prevent="submit">
    <BaseFormField
      label="Тип пометки"
      required
      :filled="Boolean(form.remark_type)"
    >
      <BaseSegmentedControl v-model="form.remark_type" :options="types" :disabled="isSubmitting" />
    </BaseFormField>
    <BaseFormField
      v-if="form.remark_type === 'defect'"
      label="Критичность"
      required
      :filled="Boolean(form.severity)"
      :error="submitted && !form.severity"
    >
      <fieldset class="vehicle-remark-form__severity">
        <legend class="vehicle-remark-form__legend">
          Выберите критичность неисправности
        </legend>
        <label
          v-for="option in severities"
          :key="option.value"
          :class="{ selected: form.severity === option.value }"
        >
          <input
            v-model="form.severity"
            type="radio"
            :name="`${formId}-severity`"
            :disabled="isSubmitting"
            :value="option.value"
          />
          <span>
            <strong class="vehicle-remark-form__severity-title">
              <span class="vehicle-remark-form__severity-dot" :style="{ backgroundColor: option.color }" aria-hidden="true" />
              {{ option.label }}
            </strong>
            <small>{{ option.description }}</small>
          </span>
        </label>
      </fieldset>
    </BaseFormField>
    <BaseFormField
      label="Название"
      :for-id="`${formId}-name`"
      required
      :filled="Boolean(form.name.trim())"
      :error="submitted && !form.name.trim()"
    >
      <base-input
        :id="`${formId}-name`"
        v-model="form.name"
        :disabled="isSubmitting"
        placeholder="Кратко опишите проблему или особенность ТС"
      />
    </BaseFormField>
    <BaseFormField label="Описание" :for-id="`${formId}-text`" required :filled="Boolean(form.text.trim())" :error="submitted && !form.text.trim()">
      <base-textarea
        :id="`${formId}-text`"
        v-model="form.text"
        :disabled="isSubmitting"
        placeholder="Когда заметили, что проверили, на что обратить внимание"
        rows="3"
      />
    </BaseFormField>
    <BaseFormField :label="remark ? undefined : 'Файлы'" :for-id="`${formId}-files`" wide>
      <AttachmentUploader
        v-model:attachment-ids="form.attachment_ids"
        :compact="!remark"
        attachable-type="Fleet::Vehicle::Remark"
        requisite-code="vehicle-remark-form"
        :existing-attachments="initialAttachments"
        :form-id="formId"
        @upload-state-changed="reading = $event.hasBlockingUploads"
      />
    </BaseFormField>
    <BaseFormField v-if="vehicleId || remark?.vehicle_id" label="Связанные ремонты" wide>
      <FleetRepairRemarkSelect v-model="form.repair_ids" resource="repairs" :vehicle-id="vehicleId || remark?.vehicle_id"
        :initial-records="remark?.repairs || []" :disabled="isSubmitting" />
    </BaseFormField>
  </form>
</template>
<script>
import FleetRepairRemarkSelect from "@/components/Fleet/RepairRemark/Select.vue";
import AttachmentUploader from "@/components/Attachment/Uploader.vue";
import { mapAssociationList } from "@/utils/associationListUtils";
import searchFields from "@/config/filters/vehicle/remarks.json";
const remarkTypes = searchFields.find((field) => field.value === "remark_type").options;
const remarkSeverities = searchFields.find((field) => field.value === "severity").options.filter((option) => option.value !== "unknown");
export default {
  name: "VehicleRemarkFormForm",
  components: { AttachmentUploader, FleetRepairRemarkSelect },
  props: {
    vehicleId: { type: [Number, String], default: null },
    remark: { type: Object, default: null },
    isSubmitting: { type: Boolean, default: false },
    formId: { type: String, default: "vehicle-remark-form" },
  },
  emits: ["submit"],
  data() {
    return {
      initialAttachments: [...(this.remark?.attachments || [])],
      submitted: false,
      reading: false,
      types: remarkTypes,
      severities: remarkSeverities,
      form: {
        repair_ids: this.remark ? mapAssociationList(this.remark.repairs, (repair) => repair.id) : [],
        attachment_ids: this.remark ? mapAssociationList(this.remark.attachments, (file) => file.id) : [],
        text: this.remark?.text || "",
        name: this.remark?.name || "",
        remark_type: this.remark?.remark_type || "defect",
        severity: this.remark?.severity || "important",
      },
    };
  },
  methods: {
    submit() {
      if (this.reading || this.isSubmitting) return;
      this.submitted = true;
      if (
        !this.form.name.trim() ||
        !this.form.text.trim() ||
        (this.form.remark_type === "defect" && !this.form.severity)
      )
        return;
      this.$emit("submit", { ...this.form, severity: this.form.remark_type === "defect" ? this.form.severity : null });
    },
  },
};
</script>
<style scoped>
.vehicle-remark-form {
  container-type: inline-size;
  container-name: remark-form;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.vehicle-remark-form__severity {
  border: 0;
  padding: 0;
  margin: 0;
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}
.vehicle-remark-form__legend {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
}
.vehicle-remark-form__severity label {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
  border: 1px solid #e2e8f0;
  padding: 10px;
  border-radius: 8px;
  cursor: pointer;
}
.vehicle-remark-form__severity label.selected {
  background: #f8faff;
  border-color: var(--color-blue);
}
.vehicle-remark-form__severity input {
  accent-color: var(--color-blue);
  margin: 2px 0 0;
}
.vehicle-remark-form__severity strong {
  font-size: 13px;
  line-height: 1.4;
}
.vehicle-remark-form__severity-title {
  display: flex;
  align-items: center;
  gap: 5px;
}
.vehicle-remark-form__severity-dot {
  width: 8px;
  height: 8px;
  flex-shrink: 0;
  border-radius: 50%;
}
.vehicle-remark-form__severity small {
  display: block;
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
  margin-top: 4px;
}
@container remark-form (max-width: 540px) {
  .vehicle-remark-form__severity {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
